import React, { useState, useEffect } from 'react';
import { FiEdit2, FiTrash2, FiPlus, FiPhone, FiUser, FiMapPin, FiDollarSign, FiSearch } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import ConfirmationModal from '../components/confirmation';
import './retailer.css';

const ViewRetailers = () => {
   const navigate = useNavigate();
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [retailerToDelete, setRetailerToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [searchInputFocused, setSearchInputFocused] = useState(false);
  

  // Debounce search term
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  // Fetch retailers with search
  useEffect(() => {
    const fetchRetailersWithPayments = async () => {
      try {
        setLoading(true);
        
        // Fetch retailers with search term
        const response = await axios.get('/retailers', {
          params: { search: debouncedSearchTerm }
        });
        const retailersData = response.data;

        // Fetch last payment for each retailer
        const retailersWithPayments = await Promise.all(
          retailersData.map(async (retailer) => {
            try {
              const paymentsResponse = await axios.get(
                `/retailers/${retailer.retailer_id}/payments`
              );
              const lastPayment = paymentsResponse.data[0];
              
              return {
                ...retailer,
                lastPaymentDate: lastPayment ? lastPayment.payment_date : null
              };
            } catch (err) {
              console.error(`Error fetching payments for retailer ${retailer.retailer_id}:`, err);
              return {
                ...retailer,
                lastPaymentDate: null
              };
            }
          })
        );

        setRetailers(retailersWithPayments);
      } catch (err) {
        setError(err.response?.data?.error || err.message || 'Failed to fetch retailers');
      } finally {
        setLoading(false);
      }
    };

    fetchRetailersWithPayments();
  }, [debouncedSearchTerm]);

  const handleDeleteClick = (id) => {
    setActionError(null);
    setRetailerToDelete(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await axios.delete(`/retailers/${retailerToDelete}`);
      setRetailers(retailers.filter(retailer => retailer.retailer_id !== retailerToDelete));
    } catch (err) {
      console.error('Error deleting retailer:', err);
      setActionError(err.response?.data?.error || 'Failed to delete retailer');
    } finally {
      setShowDeleteModal(false);
      setRetailerToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setRetailerToDelete(null);
  };

   const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };


  const handleSearchFocus = () => {
    setSearchInputFocused(true);
  };

  const handleSearchBlur = () => {
    setSearchInputFocused(false);
  };

  if (loading) {
    return <div className="loading">Loading retailers...</div>;
  }

  if (error) {
    return <div className="error">Error: {error}</div>;
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'No transactions';

    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <div className="retailers-container">
      {actionError && <div className="error">{actionError}</div>}
      <div className="retailers-header">
        <h1 className="retailers-title">Retailers</h1>
        <div className="retailers-controls">
          <div className={`search-input-container ${searchInputFocused ? 'focused' : ''}`}>
            <FiSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search retailers..."
              value={searchTerm}
              onChange={handleSearchChange}
              onFocus={handleSearchFocus}
              onBlur={handleSearchBlur}
              className="search-input"
              autoFocus={searchInputFocused}
            />
          </div>
          <button 
            className="add-retailer-btn"
            onClick={() => navigate('/retailers/add')}
          >
            <FiPlus />
            Add Retailer
          </button>
        </div>
      </div>

      <table className="retailers-table">
        <thead>
          <tr>
            <th>Retailer Name</th>
            <th>Contact Information</th>
            <th>Address</th>
            <th>Due Amount</th>
            <th>Last Transaction</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {retailers.map((retailer) => (
            <tr key={retailer.retailer_id}>
              <td>
                <div 
                  className="retailer-name clickable" 
                  onClick={() => navigate(`/retailers/details/${retailer.retailer_id}`)}
                >
                  <FiUser className="user-icon" />
                  <div>
                    {retailer.name}
                    <div className="retailer-email">{retailer.email}</div>
                  </div>
                </div>
              </td>
              <td>
                <div className="contact-info">
                  <FiPhone className="contact-icon" />
                  {retailer.contact_number}
                </div>
              </td>
              <td>
                <div className="address-info">
                  <FiMapPin className="address-icon" />
                  {retailer.address || 'No address'}
                </div>
              </td>
              <td>
                <div className={`due-amount ${retailer.due_amount > 0 ? 'positive-due' : ''}`}>
                  Rs.
                  {retailer.due_amount?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </div>
              </td>
              <td>
                <div className="last-transaction">
                  {formatDate(retailer.lastPaymentDate)}
                </div>
              </td>
              <td>
                <div className="action-buttons">
                  <button
                    onClick={() => navigate(`/retailers/edit/${retailer.retailer_id}`)}
                    className="edit-btn"
                    title="Edit"
                  >
                    <FiEdit2 size={16} />
                  </button>
                  <button 
                    onClick={() => handleDeleteClick(retailer.retailer_id)}
                    className="delete-btn"
                    title="Delete"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <ConfirmationModal
        show={showDeleteModal}
        title="Delete Retailer"
        message="Are you sure you want to delete this retailer? This action cannot be undone."
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />

      {retailers.length === 0 && (
        <div className="empty-state">
          <p>No retailers found. Add your first retailer to get started.</p>
        </div>
      )}
    </div>
  );
};

export default ViewRetailers;